import React, { useState } from 'react';
import { Link } from 'react-router-dom';

import { Button, Modal } from '@copilot/ui';
import Icon from '../common/Icon';
import { acceptTerms } from '../../services/legalApi';
import { useAuth } from '../../hooks/useAuth';

/**
 * Shown once after sign-in, until the current terms are accepted.
 *
 * # Why once, and not at every meeting
 *
 * Recording consent is a per-conversation obligation - the participants
 * differ each time - so a per-meeting checkbox looks like the stricter
 * design. It isn't, in practice. A checkbox on a screen someone passes
 * through several times a day gets clicked without being read; cookie
 * banners trained everyone to do exactly that. One serious prompt, once,
 * is read.
 *
 * The evidential record a per-meeting checkbox would have produced is
 * recovered instead by stamping the accepted version onto every meeting
 * as it is created (services/meeting_service.py), so "under which terms
 * was THIS meeting recorded" still has an answer.
 *
 * # Why it cannot be dismissed
 *
 * No close button and no Escape handler: the two things it states - that
 * obtaining consent is the user's responsibility, and that AI output is
 * unreliable - are the ones that carry real consequences if unread.
 * Everything else in this app can be dismissed; these cannot. The way
 * out is to accept, or to sign out.
 */
function TermsConsentModal() {
  const { user, updateUser, logout } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  if (!user?.needs_terms_acceptance) return null;

  const handleAccept = async () => {
    setSubmitting(true);
    setError('');

    try {
      const updated = await acceptTerms();
      // Updating auth state is what unmounts this - the modal is driven
      // entirely by needs_terms_acceptance, which the server recomputes.
      updateUser(updated);
    } catch (err) {
      setError(
        err?.message || 'We could not record that just now. Please try again.'
      );
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title="Before you start"
      subtitle="Two things to know about recording meetings with KrishvaQ."
      size="md"
      actions={
        <>
          <Button variant="ghost" onClick={logout} disabled={submitting}>
            Sign out
          </Button>
          <Button variant="primary" onClick={handleAccept} disabled={submitting}>
            {submitting ? 'Saving…' : 'I understand and agree'}
          </Button>
        </>
      }
    >
      <div className="terms-consent">
        <section className="terms-consent-point">
          <span className="terms-consent-icon">
            <Icon name="shield" size={18} />
          </span>
          <div>
            <h3>Getting consent is your responsibility</h3>
            <p>
              Recording laws differ by country and, in the United States, by
              state. In many places <strong>every participant</strong> must
              agree before a conversation may be recorded or transcribed, and
              recording without that agreement can be a criminal offence.
            </p>
            <p>
              KrishvaQ does not tell other participants that a meeting is being
              transcribed, and the desktop overlay is hidden from screen
              sharing so your notes stay private when you share your screen.
              They will not learn of it from the software — so you need to tell
              them.
            </p>
          </div>
        </section>

        <section className="terms-consent-point">
          <span className="terms-consent-icon">
            <Icon name="sparkles" size={18} />
          </span>
          <div>
            <h3>The AI gets things wrong</h3>
            <p>
              Transcripts can misrepresent what was said — accents, background
              noise and overlapping speakers all cause errors. Translations and
              AI answers can be incomplete or confidently wrong.
            </p>
            <p>
              Treat what KrishvaQ produces as a draft aid, not a record of
              fact, and never as legal, medical or financial advice.
            </p>
          </div>
        </section>

        <p className="terms-consent-links">
          By continuing you agree to our{' '}
          <Link to="/terms" target="_blank" rel="noreferrer">
            Terms of Service
          </Link>{' '}
          and{' '}
          <Link to="/privacy" target="_blank" rel="noreferrer">
            Privacy Policy
          </Link>
          . They open in a new tab, so you will not lose your place here.
        </p>

        {error && (
          <p className="terms-consent-error" role="alert">
            {error}
          </p>
        )}
      </div>
    </Modal>
  );
}

export default TermsConsentModal;
